import { useCallback, useEffect, useState } from "react";
import classNames from "classnames";
import { DndProvider } from "react-dnd-multi-backend";
import { HTML5toTouch } from "rdndmb-html5-to-touch";

import {
  QuizDragAndDropQuestion,
  QuizDragAndDropQuestionAnswer,
} from "@ascendnow/shared";

import styles from "./AscQuizDragAndDropQuestion.module.scss";
import AscImageList from "./AscImageList/AscImageList";
import AscDraggableImageCard from "./AscDraggableImageCard/AscDraggableImageCard";
import AscImageGroup from "./AscImageGroup/AscImageGroup";

export interface AscQuizDragAndDropQuestionProps {
  className?: string;
  question: QuizDragAndDropQuestion;
  attemptsCompleted?: boolean;
  loading?: boolean;
  disabled?: boolean;
  answers: QuizDragAndDropQuestionAnswer[];
  onAnswerChange: (
    questionId: string,
    answers: QuizDragAndDropQuestionAnswer[]
  ) => void;
}

export default function AscQuizDragAndDropQuestion({
  className,
  attemptsCompleted = false,
  loading = false,
  disabled = false,
  question,
  answers,
  onAnswerChange,
}: AscQuizDragAndDropQuestionProps) {
  const [imageList, setImageList] = useState<QuizDragAndDropQuestionAnswer[]>(
    []
  );

  const handleDrag = useCallback(
    function (imageId: string, questionId: string) {
      const answerBeforeDrag = [...imageList, ...answers]?.find(
        (image) => image?.imageId === imageId
      );

      if (
        !answerBeforeDrag?.questionId ||
        answerBeforeDrag?.questionId === questionId
      ) {
        return;
      }

      //drag from imageList
      else if (answerBeforeDrag?.questionId === "imageList") {
        const alreadyDragged = answers.find(
          (el) => el?.questionId === questionId
        );

        // if already dragged, replace images;
        if (alreadyDragged?.imageId) {
          onAnswerChange(question?.id, [
            ...(answers?.filter(
              (answer) => answer?.questionId !== questionId
            ) || []),
            {
              questionId,
              imageId,
            },
          ]);
          setImageList((prev) => [
            ...prev.filter((el) => el?.imageId !== imageId),
            {
              questionId: "imageList",
              imageId: alreadyDragged?.imageId,
            },
          ]);
        } else {
          setImageList((prev) =>
            prev?.filter((image) => image?.imageId !== imageId)
          );
          onAnswerChange(questionId, [
            ...answers,
            {
              questionId,
              imageId,
            },
          ]);
        }
        return;
      }

      //drag to imageList
      else if (questionId === "imageList") {
        setImageList((prev) => [
          ...prev,
          {
            questionId,
            imageId,
          },
        ]);
        onAnswerChange(
          questionId,
          answers?.filter((answer) => answer?.imageId !== imageId)
        );
        return;
      }

      //drag from answered question to another answered one
      else if (
        answers?.find((a) => a?.questionId === questionId) &&
        answers?.find((a) => a?.imageId === imageId)
      ) {
        const _answers = [...(answers || [])];

        const aIndex = _answers.findIndex((a) => a?.questionId === questionId);
        const bIndex = _answers.findIndex((a) => a?.imageId === imageId);

        //eslint-disable-next-line
        _answers[bIndex]!.imageId = _answers[aIndex]!.imageId;
        //eslint-disable-next-line 
        _answers[aIndex]!.imageId = imageId;

        onAnswerChange(questionId, _answers);
        return;
      }

      onAnswerChange(question?.id, [
        ...(answers?.filter((answer) => answer?.imageId !== imageId) || []),
        {
          questionId,
          imageId,
        },
      ]);
    },

    //eslint-disable-next-line
    [answers, imageList, onAnswerChange]
  );

  useEffect(() => {
    setImageList(
      question?.images
        ?.map((image) => ({
          questionId: "imageList",
          imageId: image?.id,
          imageUrl: image?.imageUrl,
        }))
        ?.filter((image) => !answers?.some((i) => i.imageId === image?.imageId))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question, answers]);

  return (
    <div
      data-testid="asc-quiz-drag-and-drop-question"
      className={classNames(styles.ground, className)}
    >
      <DndProvider options={HTML5toTouch}>
        <div className={styles.questionList}>
          {question?.questions?.map((q) => {
            const answer = answers?.find((i) => i.questionId === q?.id);
            return (
              <AscImageGroup
                key={q?.id}
                id={q?.id}
                title={q?.word}
                loading={loading}
                wrapperClassName={styles.dropZoneWrapper}
                correct={answer?.correct}
              >
                {answer ? (
                  <AscDraggableImageCard
                    id={answer?.imageId}
                    imageUrl={
                      question?.images?.find?.(
                        (image) => image?.id === answer?.imageId
                      )?.imageUrl || ""
                    }
                    className={styles.draggableWord}
                    onDrag={handleDrag}
                    disabled={attemptsCompleted || disabled}
                  />
                ) : null}
              </AscImageGroup>
            );
          })}
        </div>

        {imageList && Array.isArray(imageList) && !attemptsCompleted && (
          <AscImageList id={"imageList"}>
            {imageList?.map((image) => (
              <AscDraggableImageCard
                key={image?.imageId}
                id={image?.imageId}
                imageUrl={
                  question?.images?.find?.((i) => i?.id === image?.imageId)
                    ?.imageUrl || ""
                }
                onDrag={handleDrag}
                disabled={attemptsCompleted || disabled}
              />
            ))}
          </AscImageList>
        )}
      </DndProvider>
    </div>
  );
}
